import React, { useState, useRef, useEffect } from 'react';
import { X, Send, Loader2, Mail, Bold, Italic, Underline, List, Link2, Type, Users, RefreshCw } from 'lucide-react';
import axios from 'axios';

const BulkEmailModal = ({ 
  isOpen, 
  onClose, 
  onEmailSent 
}) => {
  const [loading, setLoading] = useState(false);
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [recipientCount, setRecipientCount] = useState(0);
  const [loadingCount, setLoadingCount] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const textareaRef = useRef(null);
  
  // Filter states
  const [filters, setFilters] = useState({
    subscription_types: [],
    subscription_status: null,  // NEW: Subscription status filter
    email_verification_status: null,
    license_status: null
  });

  // Message templates
  const messageTemplates = [
    {
      id: 'marketing',
      name: 'Marketing Email',
      subject: 'Exciting Updates from Portfolio Auditor',
      body: `<p>Dear Valued Customer,</p>

<p>We hope this message finds you well! We're excited to share some exciting updates about Portfolio Auditor.</p>

<h3>What's New:</h3>
<ul>
<li><strong>Feature 1:</strong> [Describe new feature]</li>
<li><strong>Feature 2:</strong> [Describe new feature]</li>
<li><strong>Feature 3:</strong> [Describe new feature]</li>
</ul>

<p><strong>We value your business</strong> and are committed to providing you with the best portfolio auditing experience.</p>

<p>[Your custom message here]</p>`
    },
    {
      id: 'general_notification',
      name: 'General Notification',
      subject: 'Important Update - Portfolio Auditor',
      body: `<p>Dear Customer,</p>

<p>We wanted to inform you about an important update regarding Portfolio Auditor.</p>

<h3>Key Information:</h3>
<p>[Your notification details here]</p>

<p>If you have any questions or concerns, please don't hesitate to reach out to our support team.</p>

<p>Thank you for your continued trust in Portfolio Auditor.</p>`
    },
    {
      id: 'content_sharing',
      name: 'Content Sharing',
      subject: 'Helpful Resources from Portfolio Auditor',
      body: `<p>Dear Customer,</p>

<p>We've put together some valuable resources that we think will help you get the most out of Portfolio Auditor.</p>

<h3>Resources:</h3>
<ul>
<li><strong>Getting Started Guide:</strong> [Add link or description]</li>
<li><strong>Best Practices:</strong> [Add link or description]</li>
<li><strong>Video Tutorials:</strong> [Add link or description]</li>
</ul>

<p>[Your custom message here]</p>

<p>We're here to help you succeed!</p>`
    },
    {
      id: 'upgrade_request',
      name: 'Upgrade License Request',
      subject: 'Upgrade Your Portfolio Auditor Experience',
      body: `<p>Dear Customer,</p>

<p>We hope you're enjoying Portfolio Auditor! We wanted to let you know about the benefits of upgrading your current plan.</p>

<h3>Premium Features Include:</h3>
<ul>
<li><strong>Extended Duration:</strong> Longer subscription period</li>
<li><strong>More PANs:</strong> Track up to 3 portfolios with Family Plan</li>
<li><strong>Priority Support:</strong> Get faster responses from our support team</li>
<li><strong>Advanced Features:</strong> Access to premium analytics and reports</li>
</ul>

<p><strong>Special Offer:</strong> [Add any promotional details]</p>

<p>Upgrade today and take your portfolio management to the next level!</p>

<p><a href="${process.env.REACT_APP_BACKEND_URL?.replace('/api', '')}/dashboard" target="_blank">Visit Your Dashboard to Upgrade</a></p>`
    }
  ];

  // Default footer for all emails
  const defaultFooter = `
<br><br>
<hr>
<p><strong>Best regards,</strong><br>
Portfolio Auditor Team<br>
<strong>Email:</strong> ${process.env.REACT_APP_COMPANY_EMAIL || 'support@portfolioauditor.com'}<br>
<strong>Website:</strong> <a href="${process.env.REACT_APP_BACKEND_URL?.replace('/api', '')}" target="_blank">Portfolio Auditor</a></p>

<p><em>This email was sent from the Portfolio Auditor Admin Panel. If you wish to unsubscribe from marketing emails, please contact us.</em></p>`;

  // Fetch recipient count when filters change
  useEffect(() => {
    if (isOpen) {
      fetchRecipientCount();
    }
  }, [filters, isOpen]);

  const fetchRecipientCount = async () => {
    setLoadingCount(true);
    try {
      const params = new URLSearchParams();
      
      if (filters.subscription_types.length > 0) {
        params.append('subscription_types', filters.subscription_types.join(','));
      }
      
      if (filters.email_verification_status) {
        params.append('email_verification_status', filters.email_verification_status);
      }
      
      if (filters.license_status) {
        params.append('license_status', filters.license_status);
      }
      
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/bulk-email/recipient-count?${params.toString()}`,
        {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        }
      );
      
      setRecipientCount(response.data.recipient_count);
    } catch (error) {
      console.error('Error fetching recipient count:', error);
      setRecipientCount(0);
    } finally {
      setLoadingCount(false);
    }
  };

  // Rich text formatting functions
  const insertFormatting = (before, after = '') => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = message.substring(start, end);
    const newText = before + selectedText + after;
    
    const newMessage = message.substring(0, start) + newText + message.substring(end);
    setMessage(newMessage);
    
    setTimeout(() => {
      textarea.focus();
      const newPosition = start + before.length + selectedText.length + after.length;
      textarea.setSelectionRange(newPosition, newPosition);
    }, 0);
  };

  const formatBold = () => insertFormatting('<strong>', '</strong>');
  const formatItalic = () => insertFormatting('<em>', '</em>');
  const formatUnderline = () => insertFormatting('<u>', '</u>');
  const formatHeading = () => insertFormatting('<h3>', '</h3>');
  const insertList = () => insertFormatting('<ul><li>', '</li></ul>');
  const insertLink = () => {
    const url = prompt('Enter URL:');
    if (url) {
      insertFormatting(`<a href="${url}">`, '</a>');
    }
  };

  // Handle template selection
  const handleTemplateSelect = (templateId) => {
    const template = messageTemplates.find(t => t.id === templateId);
    if (template) {
      setSubject(template.subject);
      setMessage(template.body);
      setSelectedTemplate(templateId);
    }
  };

  // Handle filter changes
  const handleSubscriptionTypeChange = (type) => {
    setFilters(prev => {
      const types = prev.subscription_types.includes(type)
        ? prev.subscription_types.filter(t => t !== type)
        : [...prev.subscription_types, type];
      return { ...prev, subscription_types: types };
    });
  };

  const handleEmailVerificationChange = (status) => {
    setFilters(prev => ({
      ...prev,
      email_verification_status: prev.email_verification_status === status ? null : status
    }));
  };

  const handleSubscriptionStatusChange = (status) => {
    setFilters(prev => ({
      ...prev,
      subscription_status: prev.subscription_status === status ? null : status
    }));
  };

  const handleLicenseStatusChange = (status) => {
    setFilters(prev => ({
      ...prev,
      license_status: prev.license_status === status ? null : status
    }));
  };

  // Send bulk email
  const handleSendEmail = async () => {
    if (!subject.trim() || !message.trim()) {
      alert('Please fill in both subject and message.');
      return;
    }

    if (recipientCount === 0) {
      alert('No recipients match the selected filters.');
      return;
    }

    setLoading(true);
    try {
      const emailContent = message + defaultFooter;
      
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/send-bulk-email`,
        {
          subject: subject,
          message: emailContent,
          filters: filters,
          preview_only: false
        },
        {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        }
      );

      if (onEmailSent) {
        onEmailSent(
          'success', 
          'Bulk Email Sent!', 
          `Successfully sent email to ${response.data.details.successful_sends} recipient(s)`
        );
      }
      
      setShowConfirmation(false);
      onClose();
    } catch (error) {
      console.error('Error sending bulk email:', error);
      if (onEmailSent) {
        onEmailSent(
          'error', 
          'Failed to send bulk email', 
          error.response?.data?.detail || 'Please try again later'
        );
      }
    } finally {
      setLoading(false);
    }
  };

  // Clear form
  const handleClear = () => {
    setSubject('');
    setMessage('');
    setSelectedTemplate('');
    setFilters({
      subscription_types: [],
      email_verification_status: null,
      license_status: null
    });
  };

  if (!isOpen) return null;

  // Confirmation dialog
  if (showConfirmation) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
          <div className="flex items-center mb-4">
            <Mail className="w-6 h-6 text-blue-600 mr-3" />
            <h2 className="text-xl font-semibold text-gray-900">Confirm Bulk Email</h2>
          </div>
          
          <div className="mb-6">
            <p className="text-gray-700 mb-4">
              You are about to send this email to <strong>{recipientCount} recipient(s)</strong>.
            </p>
            <div className="bg-gray-50 p-4 rounded-lg">
              <p className="text-sm text-gray-600 mb-2">
                <strong>Subject:</strong> {subject}
              </p>
              <p className="text-sm text-gray-600">
                <strong>Applied Filters:</strong>
              </p>
              <ul className="text-sm text-gray-600 ml-4 mt-1">
                {filters.subscription_types.length > 0 && (
                  <li>• Subscription Types: {filters.subscription_types.join(', ')}</li>
                )}
                {filters.subscription_status && (
                  <li>• Subscription Status: {filters.subscription_status}</li>
                )}
                {filters.email_verification_status && (
                  <li>• Email Status: {filters.email_verification_status}</li>
                )}
                {filters.license_status && (
                  <li>• License Status: {filters.license_status}</li>
                )}
                {filters.subscription_types.length === 0 && 
                 !filters.subscription_status &&
                 !filters.email_verification_status && 
                 !filters.license_status && (
                  <li>• All users (no filters applied)</li>
                )}
              </ul>
            </div>
          </div>

          <div className="flex justify-end space-x-3">
            <button
              onClick={() => setShowConfirmation(false)}
              className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 font-medium transition-colors"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              onClick={handleSendEmail}
              disabled={loading}
              className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Send Now
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-6xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center">
            <Mail className="w-6 h-6 text-blue-600 mr-3" />
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Bulk Email</h2>
              <p className="text-sm text-gray-600 mt-1">
                Send email to multiple customers at once
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            disabled={loading}
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-hidden flex">
          {/* Left Panel - Filters */}
          <div className="w-80 border-r border-gray-200 p-6 overflow-y-auto bg-gray-50">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-gray-900 flex items-center">
                <Users className="w-5 h-5 mr-2" />
                Recipient Filters
              </h3>
              <button
                onClick={fetchRecipientCount}
                disabled={loadingCount}
                className="text-blue-600 hover:text-blue-700 transition-colors"
                title="Refresh count"
              >
                <RefreshCw className={`w-4 h-4 ${loadingCount ? 'animate-spin' : ''}`} />
              </button>
            </div>

            {/* Recipient Count Display */}
            <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm text-gray-600 mb-1">Recipients:</p>
              <p className="text-2xl font-bold text-blue-600">
                {loadingCount ? '...' : recipientCount}
              </p>
              <p className="text-xs text-gray-500 mt-1">users will receive this email</p>
            </div>

            {/* Subscription Type Filter */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Subscription Type
              </label>
              <div className="space-y-2">
                {['beta', 'individual', 'family'].map((type) => (
                  <label key={type} className="flex items-center">
                    <input
                      type="checkbox"
                      checked={filters.subscription_types.includes(type)}
                      onChange={() => handleSubscriptionTypeChange(type)}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="ml-2 text-sm text-gray-700 capitalize">{type}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Subscription Status Filter - NEW */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Subscription Status
              </label>
              <div className="space-y-2">
                {[
                  { value: 'active', label: 'Active' },
                  { value: 'pending', label: 'Pending' },
                  { value: 'created', label: 'Created' },
                  { value: 'cancelled', label: 'Cancelled' },
                  { value: 'expired', label: 'Expired' },
                  { value: 'completed', label: 'Completed' },
                  { value: 'halted', label: 'Halted' },
                  { value: 'none', label: 'No Subscription (Beta)' }
                ].map((status) => (
                  <label key={status.value} className="flex items-center">
                    <input
                      type="checkbox"
                      checked={filters.subscription_status === status.value}
                      onChange={() => handleSubscriptionStatusChange(status.value)}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="ml-2 text-sm text-gray-700">{status.label}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Email Verification Status Filter */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email Verification
              </label>
              <div className="space-y-2">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={filters.email_verification_status === 'verified'}
                    onChange={() => handleEmailVerificationChange('verified')}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="ml-2 text-sm text-gray-700">Verified Only</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={filters.email_verification_status === 'unverified'}
                    onChange={() => handleEmailVerificationChange('unverified')}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="ml-2 text-sm text-gray-700">Unverified Only</span>
                </label>
              </div>
            </div>

            {/* License Status Filter - UPDATED with all statuses */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                License Status
              </label>
              <div className="space-y-2">
                {[
                  { value: 'activated', label: 'Activated' },
                  { value: 'unactivated', label: 'Not Activated' },
                  { value: 'expired', label: 'Expired' },
                  { value: 'deactivated', label: 'Deactivated' }
                ].map((status) => (
                  <label key={status.value} className="flex items-center">
                    <input
                      type="checkbox"
                      checked={filters.license_status === status.value}
                      onChange={() => handleLicenseStatusChange(status.value)}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="ml-2 text-sm text-gray-700">{status.label}</span>
                  </label>
                ))}
              </div>
            </div>

            <button
              onClick={handleClear}
              className="w-full px-4 py-2 text-sm border border-gray-300 rounded-md text-gray-700 hover:bg-gray-100 transition-colors"
            >
              Clear All Filters
            </button>
          </div>

          {/* Right Panel - Email Composer */}
          <div className="flex-1 overflow-y-auto p-6">
            <div className="space-y-6">
              {/* Template Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Email Templates</label>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  {messageTemplates.map((template) => (
                    <button
                      key={template.id}
                      onClick={() => handleTemplateSelect(template.id)}
                      className={`p-3 text-sm rounded-lg border transition-colors ${
                        selectedTemplate === template.id
                          ? 'border-blue-500 bg-blue-50 text-blue-700'
                          : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                      }`}
                      disabled={loading}
                    >
                      {template.name}
                    </button>
                  ))}
                </div>
              </div>

              {/* Subject */}
              <div>
                <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-2">
                  Subject
                </label>
                <input
                  id="subject"
                  type="text"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  placeholder="Enter email subject..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  disabled={loading}
                />
              </div>

              {/* Message */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Message
                </label>
                
                {/* Rich Text Toolbar */}
                <div className="border border-gray-300 rounded-t-md bg-gray-50 p-2 flex flex-wrap gap-1">
                  <button
                    type="button"
                    onClick={formatBold}
                    disabled={loading}
                    className="p-2 hover:bg-gray-200 rounded transition-colors disabled:opacity-50"
                    title="Bold"
                  >
                    <Bold className="w-4 h-4" />
                  </button>
                  <button
                    type="button"
                    onClick={formatItalic}
                    disabled={loading}
                    className="p-2 hover:bg-gray-200 rounded transition-colors disabled:opacity-50"
                    title="Italic"
                  >
                    <Italic className="w-4 h-4" />
                  </button>
                  <button
                    type="button"
                    onClick={formatUnderline}
                    disabled={loading}
                    className="p-2 hover:bg-gray-200 rounded transition-colors disabled:opacity-50"
                    title="Underline"
                  >
                    <Underline className="w-4 h-4" />
                  </button>
                  <button
                    type="button"
                    onClick={formatHeading}
                    disabled={loading}
                    className="p-2 hover:bg-gray-200 rounded transition-colors disabled:opacity-50"
                    title="Heading"
                  >
                    <Type className="w-4 h-4" />
                  </button>
                  <button
                    type="button"
                    onClick={insertList}
                    disabled={loading}
                    className="p-2 hover:bg-gray-200 rounded transition-colors disabled:opacity-50"
                    title="List"
                  >
                    <List className="w-4 h-4" />
                  </button>
                  <button
                    type="button"
                    onClick={insertLink}
                    disabled={loading}
                    className="p-2 hover:bg-gray-200 rounded transition-colors disabled:opacity-50"
                    title="Link"
                  >
                    <Link2 className="w-4 h-4" />
                  </button>
                </div>
                
                {/* Message Textarea */}
                <textarea
                  ref={textareaRef}
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Compose your message here..."
                  className="w-full border border-gray-300 border-t-0 rounded-b-md p-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 resize-none"
                  rows="16"
                  disabled={loading}
                />
                
                <p className="text-xs text-gray-500 mt-2">
                  Use the toolbar above to format your text. A default footer with company information will be automatically added.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-200 flex justify-end space-x-3">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 font-medium transition-colors"
            disabled={loading}
          >
            Cancel
          </button>
          <button
            onClick={() => setShowConfirmation(true)}
            disabled={loading || !subject.trim() || !message.trim() || recipientCount === 0}
            className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
          >
            <Send className="w-4 h-4 mr-2" />
            Send to {recipientCount} Recipient{recipientCount !== 1 ? 's' : ''}
          </button>
        </div>
      </div>
    </div>
  );
};

export default BulkEmailModal;
